import styled from '@emotion/styled';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import React from 'react';
import { useParams } from 'react-router-dom';

import { useAIResourcesContext } from './dashboard/ai-resources-context';
import { useDashboardDataContext } from './dashboard/dashboard-context';
import { isUserProjectLeadOrOwner } from './right-panel';
import Spacer from '../base/spacer';
import { CapabilityAuth } from '../capability-auth';
import UserNavigationMenu from '../settings/user-navigation-menu';

import { DesignToken } from '~/client/components/base/core';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import { FileDocument, MedicalHeartrateReport } from '~c/components/base/icon';
import {
  LeftNavMenu,
  LeftNavMenuItem,
  LeftNavMenuGroup,
  LeftNavFlyoutMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';
import { ProjectDisplayName } from '~c/components/project/project-name-left-nav';
import { aiDashboardUtils, dashboardRouteUtil } from '~c/routes';

interface Props {
  className?: string;
}

const FunctionalModelsLeftNavImpl: React.FC<Props> = ({ className }) => {
  const { matchRoute, navigateTo } = aiDashboardUtils.useHelper();
  const { refetchAiSystemList } = useAIResourcesContext();
  const { project } = useDashboardDataContext();
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useUserProfile();

  const dashboard = dashboardRouteUtil.useHelper();
  const [reportsExpanded, setReportsExpanded] = React.useState(false);

  const { state } = useLeftNavContext();
  const { currentRoute } = state;

  const isOrganization = project?.projectType === GQL.ProjectType.Organization;

  // Check if user can manage project settings (project lead or owner)
  const canManageSettings = isUserProjectLeadOrOwner(
    project as GQL.Project,
    userProfile?.email ?? ''
  );

  const routePrefix = {
    [aiDashboardUtils.routePrefix]: aiDashboardUtils.routePrefix,
    // [projectMembersRouteUtil.routePrefix]: projectMembersRouteUtil.routePrefix,
  };

  React.useEffect(() => {
    if (
      matchRoute('REPORT') ||
      matchRoute('AI_SYSTEM_REPORT') ||
      matchRoute('FRAMEWORK_ANALYSIS') ||
      matchRoute('ADVANCED_REPORTS') ||
      matchRoute('RISK_ASSESSMENT_REPORT') ||
      matchRoute('ACTION_REPORT') ||
      matchRoute('RISK_REDUCTION_REPORT') ||
      matchRoute('EU_AI_DEEP_DIVE_REPORT')
    ) {
      setReportsExpanded(true);
    }
  }, [matchRoute]);

  const handleReportsClick = () => {
    setReportsExpanded(!reportsExpanded);
  };

  return (
    <LeftNavMenu
      className={className}
      name={routePrefix[currentRoute ?? '']}
      header={{
        showBackButton: true,
        onClick: () => {
          refetchAiSystemList();
          dashboard.navigateTo(isOrganization ? 'COMPLIANCE' : 'PROJECTS');
        },
      }}
    >
      <LeftNavMenuItem
        selected={matchRoute('DASHBOARD')}
        icon={DashboardOutlinedIcon}
        onClick={() =>
          navigateTo('DASHBOARD', {
            params: { projectId: projectId ?? '' },
          })
        }
      >
        <ProjectDisplayName />
      </LeftNavMenuItem>
      {!isOrganization && (
        <LeftNavMenuItem
          selected={matchRoute('FUNCTIONAL_MODELS_LIST')}
          icon={ModelTrainingIcon}
          onClick={() =>
            navigateTo('FUNCTIONAL_MODELS_LIST', {
              params: { projectId: projectId ?? '' },
            })
          }
        >
          AI Use Cases
        </LeftNavMenuItem>
      )}
      <LeftNavMenuItem
        selected={matchRoute('PROJECT_MEMBERS')}
        icon={PeopleIcon}
        onClick={() =>
          navigateTo('PROJECT_MEMBERS', {
            params: { projectId: projectId ?? '' },
          })
        }
      >
        Stakeholders
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={matchRoute('DOCUMENTS')}
        icon={FolderIcon}
        onClick={() =>
          navigateTo('DOCUMENTS', {
            params: { projectId: projectId ?? '' },
          })
        }
      >
        Documents
      </LeftNavMenuItem>

      {/* Reports Menu Group — gated by report:view capability */}
      <CapabilityAuth capability="report:view">
        <LeftNavMenuGroup
          icon={MedicalHeartrateReport}
          title="Reports"
          expandedIcon={ExpandLessIcon}
          collapsedIcon={ExpandMoreIcon}
          expanded={reportsExpanded}
          onToggle={handleReportsClick}
        >
          <LeftNavFlyoutMenuItem
            icon={MedicalHeartrateReport}
            selected={matchRoute('REPORT')}
            onClick={() =>
              navigateTo('REPORT', {
                params: { projectId: projectId ?? '' },
              })
            }
          >
            Basic Reports
          </LeftNavFlyoutMenuItem>
          <LeftNavFlyoutMenuItem
            icon={FileDocument}
            selected={matchRoute('AI_SYSTEM_REPORT')}
            onClick={() =>
              navigateTo('AI_SYSTEM_REPORT', {
                params: { projectId: projectId ?? '' },
              })
            }
          >
            Advanced Reports
          </LeftNavFlyoutMenuItem>
          <LeftNavFlyoutMenuItem
            icon={FileDocument}
            selected={
              matchRoute('ADVANCED_REPORTS') ||
              matchRoute('RISK_ASSESSMENT_REPORT') ||
              matchRoute('ACTION_REPORT') ||
              matchRoute('RISK_REDUCTION_REPORT') ||
              matchRoute('EU_AI_DEEP_DIVE_REPORT')
            }
            onClick={() =>
              navigateTo('ADVANCED_REPORTS', {
                params: { projectId: projectId ?? '' },
              })
            }
          >
            Risk Intelligence Report
          </LeftNavFlyoutMenuItem>
          <LeftNavFlyoutMenuItem
            icon={FileDocument}
            selected={matchRoute('FRAMEWORK_ANALYSIS')}
            onClick={() => navigateTo('FRAMEWORK_ANALYSIS')}
          >
            Framework Analysis
          </LeftNavFlyoutMenuItem>
          <LeftNavFlyoutMenuItem
            icon={FileDocument}
            selected={matchRoute('DATA_LINEAGE_MAP')}
            onClick={() =>
              navigateTo('DATA_LINEAGE_MAP', {
                params: { projectId: projectId ?? '' },
              })
            }
          >
            Data Lineage
          </LeftNavFlyoutMenuItem>
        </LeftNavMenuGroup>
      </CapabilityAuth>

      {/* Settings - only visible to project Owner */}
      {canManageSettings && (
        <LeftNavMenuItem
          selected={matchRoute('SETTINGS')}
          icon={SettingsIcon}
          onClick={() =>
            navigateTo('SETTINGS', {
              params: { projectId: projectId ?? '' },
            })
          }
        >
          Settings
        </LeftNavMenuItem>
      )}
      <Spacer className="grow" />
      <UserNavigationMenu />
    </LeftNavMenu>
  );
};

const FunctionalModelsLeftNav = styled(FunctionalModelsLeftNavImpl)`
  .grow {
    flex-grow: 1;
  }
  .userinfo {
    padding: 16px;
    text-transform: capitalize;
  }
  .task-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .task-badge {
    background-color: ${DesignToken.status.danger};
    color: ${DesignToken.brand.primaryContrast};
    border-radius: 10px;
    padding: 2px 8px;
    font-size: 12px;
    margin-left: 8px;
    font-weight: 500;
  }

  .v2-header {
    margin-left: -10px;
  }

  .submenu-container {
    padding-left: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
  }
`;

export default FunctionalModelsLeftNav;
